import type { IceCandidateInit } from '@flowshare/protocol';
import type { PeerConnectionState } from '@flowshare/protocol';

import {
  PeerSession,
  type OutboundSignal,
  type PeerChannels,
} from './peer-session';

export interface WebRtcMeshOptions {
  selfPeerId: string;
  getConfiguration: () => RTCConfiguration;
  sendSignal: (targetPeerId: string, signal: OutboundSignal) => void;
  onPeerState: (peerId: string, state: PeerConnectionState) => void;
  onChannels: (peerId: string, channels: PeerChannels) => void;
  onChannelsLost: (peerId: string) => void;
  onError?: (peerId: string, message: string) => void;
}

/** Full mesh: one PeerSession per remote device in the room. */
export class WebRtcMesh {
  private readonly peers = new Map<string, PeerSession>();
  private destroyed = false;

  constructor(private readonly options: WebRtcMeshOptions) {}

  syncPeers(peerIds: string[]): void {
    if (this.destroyed) return;
    const wanted = new Set(peerIds.filter((id) => id !== this.options.selfPeerId));

    for (const id of [...this.peers.keys()]) {
      if (!wanted.has(id)) this.removePeer(id);
    }
    for (const id of wanted) {
      this.addPeer(id);
    }
  }

  addPeer(remotePeerId: string): void {
    if (this.destroyed || remotePeerId === this.options.selfPeerId) return;
    if (this.peers.has(remotePeerId)) return;

    // Smaller id is impolite and initiates; larger id is polite and rolls back.
    const polite = this.options.selfPeerId > remotePeerId;

    const session = new PeerSession({
      remotePeerId,
      polite,
      configuration: this.options.getConfiguration(),
      sendSignal: (signal) => this.options.sendSignal(remotePeerId, signal),
      onStateChange: (state) => this.options.onPeerState(remotePeerId, state),
      onChannels: (channels) => this.options.onChannels(remotePeerId, channels),
      onChannelsLost: () => this.options.onChannelsLost(remotePeerId),
      onError: (message) => this.options.onError?.(remotePeerId, message),
    });

    this.peers.set(remotePeerId, session);
    session.start();
  }

  removePeer(remotePeerId: string): void {
    const session = this.peers.get(remotePeerId);
    if (!session) return;
    session.close();
    this.peers.delete(remotePeerId);
  }

  handleDescription(remotePeerId: string, description: RTCSessionDescriptionInit): void {
    if (this.destroyed) return;
    if (!this.peers.has(remotePeerId)) this.addPeer(remotePeerId);
    const session = this.peers.get(remotePeerId);
    if (!session) return;
    void session.handleDescription(description);
  }

  handleCandidate(remotePeerId: string, candidate: IceCandidateInit | null): void {
    if (this.destroyed) return;
    if (!this.peers.has(remotePeerId)) this.addPeer(remotePeerId);
    const session = this.peers.get(remotePeerId);
    if (!session) return;
    void session.handleCandidate(candidate);
  }

  async peerRoundTripTime(remotePeerId: string): Promise<number | null> {
    return (await this.peers.get(remotePeerId)?.getRoundTripTime()) ?? null;
  }

  destroy(): void {
    this.destroyed = true;
    for (const session of this.peers.values()) session.close();
    this.peers.clear();
  }

  get peerCount(): number {
    return this.peers.size;
  }
}
